/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { recipientMastersService } from '../../services/recipient-masters.service';
import { uploadAttachment } from '../../services/email.service';
import { notificationsRuntimeService } from '../../services/notifications.service';
import { extractErrorMessage } from '../../utils/error-handler';

/**
 * A file for THIS fire — for everyone it reaches, or for one person.
 *
 * Two screens fire a notification by hand, and the file picker belonged on both:
 * the Trigger dialog on a notification, and the Engine Tester. Shared rather than
 * written twice, because the rules are not obvious — which store the upload goes
 * to, where the descriptor lands in the payload, and what a per-recipient file is
 * keyed on.
 *
 * Nothing here changes the notification. The upload is referenced in the payload,
 * so it travels with this one fire and the master's own attachments are untouched.
 */

/**
 * The parameter a file belongs in, read off the templates rather than guessed.
 *
 * A notification that attaches per fire says so in its own template: email as
 * `"attachments": "{documents}"`, WhatsApp as `media.fileId: "{mediaId}"`. Reading it
 * back tells the picker where an uploaded file goes, and which store to put it in —
 * an email attachment and a WhatsApp attachment are different uploads.
 */
const fileParameterOf = (master?: Record<string, any>):
  { name: string; channel: 'email' | 'whatsapp' } | null => {
  const templates = (master?.channelTemplates || {}) as Record<string, any>;
  const named = (held: unknown): string | null =>
    (typeof held === 'string' && /^\{[^{}]+\}$/.test(held.trim()))
      ? held.trim().slice(1, -1)
      : null;

  const email = named(templates.email?.en?.attachments);
  if (email) return { name: email, channel: 'email' };

  const whatsapp = named(templates.whatsapp?.en?.media?.fileId);
  if (whatsapp) return { name: whatsapp, channel: 'whatsapp' };

  return null;
};

interface TriggerAttachmentsProps {
  /** The notification being fired — its templates say where a file goes. */
  master?: Record<string, any>;
  /** The payload as it stands. */
  payload: Record<string, unknown>;
  /** The payload with the uploaded file in it. */
  onChange: (next: Record<string, unknown>) => void;
  /** Shown beside a resolved person, e.g. the rule they came from. */
  ruleLabel?: (recipientMasterId: string) => string;
  testId: string;
}

const TriggerAttachments = ({
  master, payload, onChange, ruleLabel, testId,
}: TriggerAttachmentsProps) => {
  const target = fileParameterOf(master);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Empty means the file is for everyone this fire reaches. Filled, it names one
  // recipient, and only their message carries it.
  const [recipientKey, setRecipientKey] = useState('');
  // Who this fire would reach, asked before firing. Empty until somebody asks:
  // resolution runs real function chains, so it is a button rather than something
  // that happens every time the screen opens.
  const [audience, setAudience] = useState<{ value: string; label: string }[] | null>(null);
  const [previewing, setPreviewing] = useState(false);

  if (!target) return null;          // nowhere for a file to go; a dead picker is worse

  /** Who this notification would reach, for the payload as it stands.
   *
   *  A per-recipient file has to be keyed on somebody's identity, and nobody knows a
   *  user id by heart. Every rule is resolved against the current payload — the same
   *  resolution a fire performs, stopped before delivery — so the picker offers real
   *  people instead of asking for an id. */
  const loadAudience = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const rules = (master?.recipients || []) as any[];
      const previews = await Promise.all(rules.map((rule) =>
        recipientMastersService
          .previewResolution(String(rule.recipientMasterId), { payload })
          .catch(() => null)));

      const seen = new Set<string>();
      const people: { value: string; label: string }[] = [];
      previews.forEach((preview, index) => {
        (preview?.parties || []).forEach((party) => {
          // The key the engine matches on, preferring the id a caller is likeliest
          // to hold. A party with neither is unaddressable by this mechanism.
          const key = String(party.userId || party.personnelMasterId || '');
          if (!key || seen.has(key)) return;
          seen.add(key);
          const contact = party.contact || {};
          const who = party.name || contact.email || contact.phone || key;
          const rule = preview?.recipientCode
            || ruleLabel?.(String(rules[index]?.recipientMasterId))
            || 'this notification';
          people.push({ value: key, label: `${who} — ${rule}` });
        });
      });
      setAudience(people);
      if (!people.length) {
        setError('This payload resolves to nobody — fire it as it is, or fix the payload.');
      }
    } catch (e) {
      setError(extractErrorMessage(e, 'The recipients could not be resolved.'));
    } finally {
      setPreviewing(false);
    }
  };

  /** Upload a file and put it in the payload, where this notification expects it.
   *
   *  Email takes a list of descriptors, WhatsApp a single file id — the shapes differ
   *  because the channels do, and the template already said which is which. */
  const attach = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const next = { ...payload };

      // Named recipient or everybody. `byRecipient` is addressing, not content: the
      // engine strips it before rendering and applies the matching entry over the
      // shared values, so a file left here reaches that one person's message.
      const only = recipientKey.trim();
      let scope: Record<string, unknown> = next;
      if (only) {
        const held = next.byRecipient;
        const block = (held && typeof held === 'object' && !Array.isArray(held)
          ? { ...(held as Record<string, any>) } : {}) as Record<string, any>;
        const mine = { ...(block[only] && typeof block[only] === 'object' ? block[only] : {}) };
        block[only] = mine;
        next.byRecipient = block;
        scope = mine;
      }

      if (target.channel === 'email') {
        const uploaded = await uploadAttachment(file);
        const held = scope[target.name];
        const existing = Array.isArray(held) ? held : [];
        scope[target.name] = [...existing, {
          fileId: uploaded.fileId, filename: uploaded.filename,
          mimeType: uploaded.mimeType, sizeBytes: uploaded.sizeBytes,
        }];
      } else {
        const uploaded = await notificationsRuntimeService.uploadWhatsappMedia(file);
        scope[target.name] = uploaded.fileId;   // one attachment per WhatsApp message
      }

      onChange(next);
    } catch (e) {
      setError(extractErrorMessage(e, 'The file could not be attached.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-1.5">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Attach a file
        <span className="ml-1 font-normal text-gray-400">
          → <span className="font-mono">{target.name}</span>
          {' '}({target.channel === 'email' ? 'email attachment' : 'WhatsApp media'})
        </span>
      </label>

      <div className="flex items-center gap-2">
        <label className="text-xs text-ink-muted whitespace-nowrap">Only for</label>
        <Dropdown
          value={recipientKey}
          options={[{ value: '', label: 'Everyone this fire reaches' }, ...(audience || [])]}
          optionValue="value"
          optionLabel="label"
          editable                 /* an id typed by hand still works */
          onChange={(e) => setRecipientKey(e.value ?? '')}
          placeholder="Everyone this fire reaches"
          className="w-full text-xs"
          data-testid={`${testId}.Only`}
        />
        <Button
          label={previewing ? 'Resolving…' : (audience ? 'Refresh' : 'Load recipients')}
          severity="secondary"
          outlined
          disabled={previewing}
          className="whitespace-nowrap text-xs"
          data-testid={`${testId}.LoadRecipients`}
          onClick={() => void loadAudience()}
        />
      </div>

      <input
        type="file"
        disabled={busy}
        data-testid={testId}
        className="text-sm file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm file:text-gray-700 dark:file:bg-gray-700 dark:file:text-gray-200"
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          e.target.value = '';           // so the same file can be retried
          if (chosen) void attach(chosen);
        }}
      />
      {busy && <small className="text-xs text-gray-400">Uploading…</small>}
      {error && <Message severity="error" className="w-full" text={error} />}
      <small className="text-xs text-gray-400">
        Uploaded now and referenced in the payload, so this send carries it and the
        notification's own attachments are unchanged. Named a recipient? The file goes
        under <span className="font-mono">byRecipient</span> and reaches only their
        message — everyone else gets the shared payload.
      </small>
    </div>
  );
};

export default TriggerAttachments;
